import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchJobCategories,
    createJobCategory,
    updateJobCategory,
    deleteJobCategory
} from '../redux/JobCategoriesSlice';
import { Plus, Edit, Trash2, ChevronRight, X, ArrowLeft, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Oval } from 'react-loader-spinner';

const JobCategories = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate()
    const { items: categories, loading, error } = useSelector(state => state.jobCategories);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isViewDetailsModalOpen, setIsViewDetailsModalOpen] = useState(false);
    const [categoryToView, setCategoryToView] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false)
    const [isAdding, setIsAdding] = useState(false)
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        description: '',
        parent_id: ''
    });

    useEffect(() => {
        dispatch(fetchJobCategories());
    }, [dispatch]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name) {
            toast.error("Category name is required");
            return;
        }

        try {
            if (selectedCategory) {
                setIsUpdating(true);
                await dispatch(updateJobCategory({ id: selectedCategory.id, data: formData }));
                toast.success("Category updated successfully");
            } else {
                setIsAdding(true);
                await dispatch(createJobCategory(formData));
                toast.success("Category created successfully");
            }

            setIsModalOpen(false);
            resetForm();
        } catch (error) {
            console.error("Error submitting form:", error);
            toast.error(error?.message || "Something went wrong. Please try again.");
        } finally {
            setIsUpdating(false);
            setIsAdding(false);
        }
    };

    const openDeleteModal = (category) => {
        setCategoryToView(category);
        setIsViewDetailsModalOpen(true);
    };

    const handleDelete = async () => {
        if (categoryToView) {
            await dispatch(deleteJobCategory(categoryToView.id));
            setIsViewDetailsModalOpen(false);
            setCategoryToView(null);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            slug: '',
            description: '',
            parent_id: ''
        });
        setSelectedCategory(null);
    };

    const openEditModal = (category) => {
        setSelectedCategory(category);
        setFormData({
            name: category.name,
            slug: category.slug,
            description: category.description,
            parent_id: category.parent_id || '',
            parent_id: category.parent_id ? category.parent_id.id : "",

        });
        setIsModalOpen(true);
    };
    // useEffect(() => {
    //     if (selectedCategory) {
    //         setFormData({
    //             name: selectedCategory.name,
    //             description: selectedCategory.description,
    //             parent_id: selectedCategory.parent_id ? selectedCategory.parent_id.id : "",
    //         });
    //     } else {
    //         setFormData({
    //             name: "",
    //             description: "",
    //             parent_id: "",
    //         });
    //     }
    // }, [selectedCategory]);
    
    // Delete Confirmation Modal
    const CategoryDetail = () => (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4">
                <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg">
                    <h2 className="text-xl font-bold mb-4">Job Category Details</h2>
                    <div className="space-y-2">
                        <p><span className="font-semibold">ID:</span> {categoryToView?.id}</p>
                        <p><span className="font-semibold">Name:</span> {categoryToView?.name}</p>
                        <p><span className="font-semibold">Description:</span> {categoryToView?.description}</p>
                        <p><span className="font-semibold">Created At:</span> {new Date(categoryToView?.created_at).toLocaleString()}</p>
                        {categoryToView.parent_id ? (
                            <div className="mt-4 p-4 bg-gray-100 rounded">
                                <h3 className="font-semibold">Parent Category</h3>
                                <p><span className="font-semibold">ID:</span> {categoryToView?.parent_id.id}</p>
                                <p><span className="font-semibold">Name:</span> {categoryToView?.parent_id.name}</p>
                            </div>
                        ) : (
                            <p className="text-gray-500 italic">No parent category</p>
                        )}
                    </div>
                    <button
                        className="mt-4 w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600"
                        onClick={()=>setIsViewDetailsModalOpen(false)}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="container mx-auto p-4">
            <button
                onClick={() => navigate('/dashboard/jobs')}
                className="flex items-center text-gray-600 hover:text-gray-900 mb-2"
            >
                <ArrowLeft size={20} className="mr-2" />
                Back
            </button>
            <div className="bg-white rounded-lg shadow-md">
                {/* Header */}
                <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                    <h1 className="text-2xl font-semibold text-gray-800">Job Categories</h1>
                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="bg-[#1330C5] text-white px-4 py-2 rounded-md flex items-center gap-2 hover:bg-[#1330C5]/90"
                    >
                        <Plus size={20} />
                        Add Category
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {loading && (
                        <div className="text-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1330C5] mx-auto"></div>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 text-red-500 p-4 rounded-md text-center">
                            {error}
                        </div>
                    )}

                    <div className="divide-y divide-gray-200">
                        {categories.map(category => (
                            <div key={category.id} className="py-4 flex items-center justify-between">
                                <div>
                                    <h3 className="text-base shadow font-medium bg-blue-100 w-fit py-1 px-2 mb-2 rounded text-blue-800">{category.name}</h3>
                                    <p className="text-sm text-gray-500">{category.description}</p>
                                    <p className="text-xs text-gray-400">
                                        Created: {new Date(category.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => openEditModal(category)}
                                        className="p-2 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100"
                                    >
                                        <Edit size={20} />
                                    </button>
                                    <button
                                        onClick={() => openDeleteModal(category)}
                                        className="p-2 text-blue-600 hover:text-blue-900 rounded-full hover:bg-blue-50"
                                    >
                                        <Eye size={20} />
                                    </button>
                                    <ChevronRight size={20} className="text-gray-400" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Create/Edit Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-40">
                    <div className="bg-white rounded-lg w-full max-w-md">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h2 className="text-xl font-semibold text-gray-800">
                                {selectedCategory ? 'Edit Category' : 'Add New Category'}
                            </h2>
                            <button
                                onClick={() => {
                                    setIsModalOpen(false);
                                    resetForm();
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <form className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Name
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#1330C5] focus:border-transparent"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Description
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#1330C5] focus:border-transparent"
                                    rows={3}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Parent Category
                                </label>
                                <select
                                    value={formData.parent_id}
                                    onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#1330C5] focus:border-transparent"
                                >
                                    <option value="">None</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex justify-end gap-2 pt-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsModalOpen(false);
                                        resetForm();
                                    }}
                                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    type="button"
                                    className="px-4 py-2 bg-[#1330C5] text-white rounded-md hover:bg-[#1330C5]/90 flex items-center justify-center"
                                    disabled={isUpdating || isAdding}
                                >
                                    {(isUpdating || isAdding) ? (
                                        <>
                                            <div className=" flex justify-center items-center gap-2">
                                                <Oval
                                                    visible={true}
                                                    height="20"
                                                    width="20"
                                                    color="#ffff"
                                                    ariaLabel="oval-loading"
                                                    wrapperStyle={{}}
                                                    wrapperClass=""
                                                />
                                                {selectedCategory ? "Updating..." : "Creating..."}

                                            </div>
                                        </>
                                    ) : (
                                        selectedCategory ? "Update" : "Create"
                                    )}
                                </button>

                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isViewDetailsModalOpen && <CategoryDetail />}
        </div>
    );
};

export default JobCategories;